import { Head, Link, router, usePage } from '@inertiajs/react'
import { type FormEvent, useState } from 'react'

import { inertiaMessageList } from '../../lib/inertiaErrors'

type PageProps = {
  /** Validation messages from the controller, or `{}` from Inertia shared errors hash when empty */
  errors?: string[] | Record<string, string>
  flash?: {
    notice?: string | null
    alert?: string | null
  }
}

export default function AdminPasswordEdit() {
  const { errors, flash } = usePage<PageProps>().props
  const [processing, setProcessing] = useState(false)
  const list = inertiaMessageList(errors)

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const data = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>
    setProcessing(true)
    router.patch('/admin/password', data, {
      preserveScroll: true,
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <>
      <Head title="Change password" />
      <div className="mx-auto flex max-w-md flex-col gap-8 py-12 md:py-16">
        <div>
          <p className="mb-3 font-mono text-xs">
            <Link href="/admin" className="text-primary-container hover:underline">
              Back to dashboard
            </Link>
          </p>
          <h1 className="font-headline text-2xl font-bold tracking-tight text-on-surface">Change password</h1>
          <p className="mt-2 font-body text-sm text-on-surface-variant">
            Enter your current password, then choose a new one.
          </p>
        </div>

        {flash?.alert ? (
          <p
            className="rounded-md border border-error/40 bg-error-container/15 px-4 py-3 font-mono text-sm text-error"
            role="alert"
          >
            {flash.alert}
          </p>
        ) : null}

        {list.length > 0 ? (
          <ul
            className="list-inside list-disc space-y-1 rounded-md border border-error/40 bg-error-container/15 px-4 py-3 font-mono text-sm text-error"
            role="alert"
          >
            {list.map((msg, i) => (
              <li key={`${i}-${msg}`}>{msg}</li>
            ))}
          </ul>
        ) : null}

        <form
          onSubmit={onSubmit}
          className="space-y-6 border border-outline-variant/15 bg-surface-container-low p-8 rounded-lg"
        >
          <div className="space-y-2">
            <label
              htmlFor="current-password"
              className="block font-label text-xs font-bold tracking-widest text-on-surface-variant uppercase"
            >
              Current password
            </label>
            <input
              id="current-password"
              name="current_password"
              type="password"
              autoComplete="current-password"
              required
              className="form-well w-full px-4 py-3 font-mono text-sm"
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="new-password"
              className="block font-label text-xs font-bold tracking-widest text-on-surface-variant uppercase"
            >
              New password
            </label>
            <input
              id="new-password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              className="form-well w-full px-4 py-3 font-mono text-sm"
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="password-confirmation"
              className="block font-label text-xs font-bold tracking-widest text-on-surface-variant uppercase"
            >
              Confirm new password
            </label>
            <input
              id="password-confirmation"
              name="password_confirmation"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              className="form-well w-full px-4 py-3 font-mono text-sm"
            />
          </div>
          <button type="submit" disabled={processing} className="key-btn-primary w-full disabled:opacity-60">
            Update password
          </button>
        </form>
      </div>
    </>
  )
}
